import React, { useState, useEffect, useCallback, useRef } from 'react';
import Header from './Header';
import Footer from './Footer';
import DocumentUpload from './DocumentUpload';
import DebtOverview from './DebtOverview';
import VerificationScreen from './VerificationScreen';
import ReinventLoyaltyHighlight from './ReinventLoyaltyHighlight';
import { useSession } from '../contexts/SessionContext';
import PaymentArrangementForm from './PaymentArrangementForm';
import { getSettlementQuote, submitSettlementOffer, getSettlementStatus, submitNegotiation, submitObjection, scheduleContact } from '../services/api';
import '../styles/mobile.css';
import '../styles/components.css';

// ─── Chevron SVG ───────────────────────────────────────────────
function ChevronDown({ className }) {
  return (
    <svg className={className} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

// ─── Accordion Wrapper ─────────────────────────────────────────
function AccordionSection({ id, icon, iconClass, title, subtitle, isOpen, onToggle, children, requiresVerification, isVerified, onVerifyClick }) {
  const needsVerify = requiresVerification && !isVerified;

  const handleClick = () => {
    if (needsVerify) {
      onVerifyClick();
      return;
    }
    onToggle(id);
  };

  return (
    <div data-testid={`accordion-${id}`}>
      <button
        type="button"
        className={`accordion-trigger ${isOpen ? 'accordion-trigger--open' : ''}`}
        onClick={handleClick}
        aria-expanded={isOpen}
        aria-controls={`accordion-body-${id}`}
      >
        <div className="accordion-trigger__left">
          <div className={`accordion-trigger__icon ${iconClass}`} aria-hidden="true">
            {icon}
          </div>
          <div className="accordion-trigger__text">
            <div className="accordion-trigger__title">{title}</div>
            <div className="accordion-trigger__subtitle">
              {needsVerify ? 'Verify identity to access' : subtitle}
            </div>
          </div>
        </div>
        <ChevronDown className="accordion-trigger__chevron" />
      </button>
      {isOpen && (
        <div className="accordion-body" id={`accordion-body-${id}`}>
          {children}
        </div>
      )}
    </div>
  );
}

// ─── Shared ContactFields ─────────────────────────────────────
export function ContactFields({ email, phone, onEmailChange, onPhoneChange, errors = {} }) {
  return (
    <div className="contact-fields">
      <div className="form-group">
        <label className="form-label" htmlFor="email">Email address</label>
        <input id="email" type="email" className={`form-input ${errors.email ? 'form-input--error' : ''}`}
          value={email} onChange={(e) => onEmailChange(e.target.value)}
          placeholder="you@example.com" required autoComplete="email" inputMode="email" />
        {errors.email && <p className="form-error">{errors.email}</p>}
      </div>
      <div className="form-group">
        <label className="form-label" htmlFor="phone">Phone number</label>
        <input id="phone" type="tel" className={`form-input ${errors.phone ? 'form-input--error' : ''}`}
          value={phone} onChange={(e) => onPhoneChange(e.target.value)}
          placeholder="072 123 4567" required autoComplete="tel" inputMode="tel" />
        {errors.phone && <p className="form-error">{errors.phone}</p>}
      </div>
    </div>
  );
}

// ─── Settle Section ────────────────────────────────────────────
const SETTLEMENT_POLL_INTERVAL_MS = 15 * 1000; // 15 seconds
const SETTLEMENT_POLL_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes
const SETTLEMENT_TERMINAL_STATUSES = ['paid', 'expired'];

function SettleContent({ email, phone, onEmailChange, onPhoneChange, loanApplicationId }) {
  const { sessionToken } = useSession();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState(null);

  // ── Settlement status polling ──
  const [settlementStatus, setSettlementStatus] = useState(null);
  const [settlementMessage, setSettlementMessage] = useState(null);
  const settlementPollRef = useRef(null);
  const settlementTimeoutRef = useRef(null);

  const stopSettlementPolling = useCallback(() => {
    if (settlementPollRef.current) {
      clearInterval(settlementPollRef.current);
      settlementPollRef.current = null;
    }
    if (settlementTimeoutRef.current) {
      clearTimeout(settlementTimeoutRef.current);
      settlementTimeoutRef.current = null;
    }
  }, []);

  // Start polling when settlement is accepted
  useEffect(() => {
    if (!accepted || !loanApplicationId) return;

    const pollStatus = async () => {
      try {
        const result = await getSettlementStatus(loanApplicationId, sessionToken);
        if (result.status) {
          setSettlementStatus(result.status);
          if (result.message) setSettlementMessage(result.message);

          if (SETTLEMENT_TERMINAL_STATUSES.includes(result.status)) {
            stopSettlementPolling();
          }
        }
      } catch {
        // Non-critical: keep polling on transient errors
      }
    };

    // Initial poll
    pollStatus();

    // Set up interval
    settlementPollRef.current = setInterval(pollStatus, SETTLEMENT_POLL_INTERVAL_MS);

    // Stop polling after 5 minutes
    settlementTimeoutRef.current = setTimeout(() => {
      stopSettlementPolling();
    }, SETTLEMENT_POLL_TIMEOUT_MS);

    return stopSettlementPolling;
  }, [accepted, loanApplicationId, sessionToken, stopSettlementPolling]);

  useEffect(() => {
    if (!loanApplicationId) return;
    let cancelled = false;
    setLoading(true); setError(null);
    getSettlementQuote(loanApplicationId)
      .then((data) => {
        if (!cancelled) {
          setQuote(data);
          // If already accepted (has payment URL), show accepted state
          if (data.status === 'accepted' && data.netcashPaymentUrl) setAccepted(true);
        }
      })
      .catch((err) => { if (!cancelled) setError(err.message || 'Unable to load settlement details.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [loanApplicationId]);

  const handleAccept = async () => {
    setAccepting(true); setError(null);
    try {
      const result = await submitSettlementOffer({ loanApplicationId, offeredAmount: quote.preApprovedAmount, email, phone });
      setQuote(result);
      setAccepted(true);
    } catch (err) { setError(err.message || 'Unable to process settlement. Please try again.'); }
    finally { setAccepting(false); }
  };

  const fmt = (amount) => amount == null ? 'R0.00' : `R${Number(amount).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <>
      <p>Pay your outstanding balance in full and resolve your account immediately. Settling protects your credit record from further impact.</p>

      {loading && <div className="section--loading"><div className="spinner" /><p>Loading settlement details...</p></div>}
      {error && (
        <div className="notice notice--danger"><p>{error}</p>
          <button type="button" className="btn btn--outline" style={{ marginTop: 12 }}
            onClick={() => { setError(null); setLoading(true); getSettlementQuote(loanApplicationId).then(setQuote).catch(e => setError(e.message)).finally(() => setLoading(false)); }}>Try Again</button>
        </div>
      )}

      {quote && !loading && (
        <>
          <div className="settlement-amount" data-testid="settlement-amount-display">
            <div className="settlement-amount__label">Settlement Amount</div>
            <div className="settlement-amount__value">{fmt(quote.preApprovedAmount)}</div>
            {quote.savingsAmount > 0 && <div className="settlement-amount__savings">You save {fmt(quote.savingsAmount)} ({quote.discountPercentage}% discount)</div>}
          </div>
          <div className="settlement-breakdown">
            <div className="settlement-breakdown__row"><span className="settlement-breakdown__label">Outstanding balance</span><span className="settlement-breakdown__value">{fmt(quote.outstandingBalance)}</span></div>
            <div className="settlement-breakdown__row"><span className="settlement-breakdown__label">Discount ({quote.discountPercentage}%)</span><span className="settlement-breakdown__value settlement-breakdown__value--discount">&minus;{fmt(quote.savingsAmount)}</span></div>
            <div className="settlement-breakdown__row settlement-breakdown__row--total"><span className="settlement-breakdown__label">Amount to pay</span><span className="settlement-breakdown__value">{fmt(quote.preApprovedAmount)}</span></div>
          </div>

          {accepted ? (
            <>
              <div className="notice notice--success"><p>A secure payment link for <strong>{fmt(quote.preApprovedAmount)}</strong> has been sent to your <strong>phone via SMS</strong> and <strong>email</strong>. Please check your messages to complete your payment through Netcash.</p></div>
              <div className="notice notice--info"><p>Payment is processed securely through <strong>Netcash</strong>. The link is valid for 48 hours. If you don't receive it, please check your spam folder or contact us.</p></div>
              <div className="settlement-status-polling" data-testid="settlement-status-polling">
                {settlementStatus === 'paid' ? (
                  <div className="notice notice--success"><p><strong>Payment received!</strong> Your settlement has been processed successfully.</p></div>
                ) : settlementStatus === 'expired' ? (
                  <div className="notice notice--danger"><p><strong>Offer expired.</strong> Please contact us or submit a new settlement request.</p></div>
                ) : (
                  <div className="notice notice--info" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div className="spinner" style={{ width: 20, height: 20, flexShrink: 0 }} />
                    <p style={{ margin: 0 }}>Waiting for payment... {settlementMessage || 'We will update this page once your payment is confirmed.'}</p>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="notice notice--info"><p>Accept this offer to receive a secure <strong>Netcash</strong> payment link via SMS and email. The offer is valid for 48 hours.</p></div>
              <hr className="section-divider" />
              <ContactFields email={email} phone={phone} onEmailChange={onEmailChange} onPhoneChange={onPhoneChange} />
              <button type="button" className="btn btn--success" onClick={handleAccept} disabled={accepting}>
                {accepting ? 'Processing...' : `Accept & Pay ${fmt(quote.preApprovedAmount)}`}
              </button>
            </>
          )}
        </>
      )}
      {!quote && !loading && !error && (
        <><ContactFields email={email} phone={phone} onEmailChange={onEmailChange} onPhoneChange={onPhoneChange} /><button type="button" className="btn btn--primary">Request Settlement Quote</button></>
      )}
    </>
  );
}

// ─── Negotiate Section ─────────────────────────────────────────
const NEGOTIATION_TYPES = [
  { value: '', label: 'Select negotiation type...' },
  { value: 'lower_settlement', label: 'Lower settlement amount' },
  { value: 'reduced_balance', label: 'Request a reduced settlement balance' },
  { value: 'extended_term', label: 'Request extended repayment term' },
];

function NegotiateContent({ email, phone, onEmailChange, onPhoneChange, loanApplicationId }) {
  const [negotiationType, setNegotiationType] = useState('');
  const [reason, setReason] = useState('');
  const [proposedAmount, setProposedAmount] = useState('');
  const [proposedTermMonths, setProposedTermMonths] = useState('');
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const e = {};
    if (!negotiationType) e.negotiationType = 'Please select a negotiation type.';
    if (!reason || reason.trim().length < 20) e.reason = 'Please provide a detailed reason (minimum 20 characters).';
    if ((negotiationType === 'lower_settlement' || negotiationType === 'reduced_balance') && (!proposedAmount || isNaN(parseFloat(proposedAmount)) || parseFloat(proposedAmount) <= 0)) e.proposedAmount = 'A proposed amount is required.';
    if (negotiationType === 'extended_term') { const m = parseInt(proposedTermMonths, 10); if (!proposedTermMonths || isNaN(m) || m < 3 || m > 60) e.proposedTermMonths = 'Proposed term must be between 3 and 60 months.'; }
    if (!email || !email.includes('@')) e.email = 'A valid email address is required.';
    if (!phone || phone.replace(/\s/g, '').length < 10) e.phone = 'A valid phone number is required.';
    setErrors(e); return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setSubmitting(true); setSubmitResult(null);
    try {
      const payload = { loanApplicationId: loanApplicationId || '', negotiationType, reason: reason.trim(), email, phone, supportingDocumentIds: files.map((_, i) => `doc-${i + 1}`) };
      if (proposedAmount) { const a = parseFloat(proposedAmount); if (!isNaN(a) && a > 0) payload.proposedAmount = a; }
      if (proposedTermMonths) { const m = parseInt(proposedTermMonths, 10); if (!isNaN(m)) payload.proposedTermMonths = m; }
      setSubmitResult(await submitNegotiation(payload));
    } catch (err) { setSubmitResult({ success: false, message: err.message || 'Unable to submit.' }); }
    finally { setSubmitting(false); }
  };

  const reset = () => { setSubmitResult(null); setNegotiationType(''); setReason(''); setProposedAmount(''); setProposedTermMonths(''); setFiles([]); setErrors({}); };

  if (submitResult?.success) return (
    <div data-testid="negotiate-success"><div className="notice notice--success"><p><strong>Reference:</strong> {submitResult.referenceNumber}</p><p>{submitResult.message}</p>{submitResult.estimatedBusinessDays && <p>Expected response within <strong>{submitResult.estimatedBusinessDays} business days</strong>.</p>}</div>
    {submitResult.comparison && <div data-testid="negotiate-comparison" className="negotiate-comparison"><p>Current balance: R{submitResult.comparison.currentOutstandingBalance?.toLocaleString('en-ZA')}</p><p>Proposed: R{submitResult.comparison.proposedBalance?.toLocaleString('en-ZA')}</p>{submitResult.comparison.potentialSavings > 0 && <p>Potential savings: R{submitResult.comparison.potentialSavings?.toLocaleString('en-ZA')}</p>}</div>}
    <button type="button" className="btn btn--outline" data-testid="negotiate-new-btn" onClick={reset}>Submit Another</button></div>
  );

  return (
    <div data-testid="section-negotiate">
      <p>Submit a proposal with supporting documents to negotiate your account terms.</p>
      {submitResult && !submitResult.success && <div className="notice notice--danger" data-testid="negotiate-error"><p>{submitResult.message}</p></div>}
      <div className="form-group">
        <label className="form-label" htmlFor="negotiate-type">What would you like to negotiate?</label>
        <select id="negotiate-type" data-testid="negotiate-type-select" className={`form-select ${errors.negotiationType ? 'form-select--error' : ''}`}
          value={negotiationType} onChange={(e) => { setNegotiationType(e.target.value); setProposedAmount(''); setProposedTermMonths(''); setErrors({}); setSubmitResult(null); }}>
          {NEGOTIATION_TYPES.map(t => <option key={t.value} value={t.value} disabled={t.value === ''}>{t.label}</option>)}
        </select>
        {errors.negotiationType && <p className="form-error">{errors.negotiationType}</p>}
      </div>

      {(negotiationType === 'lower_settlement' || negotiationType === 'reduced_balance') && (
        <div className="form-group"><label className="form-label" htmlFor="neg-amt">Proposed amount (R)</label>
          <input id="neg-amt" data-testid="negotiate-proposed-amount" type="number" className={`form-input ${errors.proposedAmount ? 'form-input--error' : ''}`} value={proposedAmount} onChange={e => setProposedAmount(e.target.value)} placeholder="e.g. 15000" min="1" inputMode="numeric" />
          {errors.proposedAmount && <p className="form-error">{errors.proposedAmount}</p>}
        </div>
      )}

      {negotiationType === 'extended_term' && (
        <div className="form-group" data-testid="negotiate-fields-extended-term"><label className="form-label" htmlFor="neg-term">Proposed term (months)</label>
          <input id="neg-term" data-testid="negotiate-term-months" type="number" className={`form-input ${errors.proposedTermMonths ? 'form-input--error' : ''}`} value={proposedTermMonths} onChange={e => setProposedTermMonths(e.target.value)} placeholder="e.g. 24" min="3" max="60" inputMode="numeric" />
          <p className="form-hint">Between 3 and 60 months</p>{errors.proposedTermMonths && <p className="form-error">{errors.proposedTermMonths}</p>}
        </div>
      )}

      {negotiationType && (
        <>
          <div className="form-group"><label className="form-label" htmlFor="neg-reason">Reason</label>
            <textarea id="neg-reason" data-testid="negotiate-reason" className={`form-textarea ${errors.reason ? 'form-textarea--error' : ''}`} placeholder="Describe your circumstances (min 20 characters)..." rows={4} value={reason} onChange={e => setReason(e.target.value)} />
            <p className="form-hint">{reason.length}/20 characters minimum</p>{errors.reason && <p className="form-error">{errors.reason}</p>}
          </div>
          <div className="form-group"><label className="form-label">Supporting documents (max 5, 10MB each, PDF/JPG/PNG)</label><DocumentUpload files={files} onChange={setFiles} /></div>
          <hr className="section-divider" />
          <ContactFields email={email} phone={phone} onEmailChange={onEmailChange} onPhoneChange={onPhoneChange} errors={{ email: errors.email, phone: errors.phone }} />
          <button type="button" className="btn btn--primary" data-testid="negotiate-btn" onClick={handleSubmit} disabled={submitting}>{submitting ? 'Submitting...' : 'Submit Negotiation'}</button>
        </>
      )}
    </div>
  );
}

// ─── Objection Section ─────────────────────────────────────────
const OBJECTION_CATEGORIES = [
  { value: 'loan_agreement', label: 'Request loan agreement', hint: 'I need a copy of my original loan agreement.' },
  { value: 'credit_bureau', label: 'Credit bureau dispute', hint: 'I want to dispute credit bureau information.' },
  { value: 'query_balance', label: 'Query balance', hint: 'The balance does not match my records.' },
  { value: 'payment_not_reflected', label: 'Payment not reflected', hint: 'A payment has not been credited.' },
  { value: 'general_complaint', label: 'General complaint', hint: 'I have a general concern.' },
  { value: 'other', label: 'Other', hint: 'My issue is not listed above.' },
];

function ObjectContent({ email, phone, onEmailChange, onPhoneChange, loanApplicationId }) {
  const [selectedType, setSelectedType] = useState('');
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!selectedType) e.selectedType = 'Please select a category.';
    if (!description || description.trim().length < 20) e.description = 'Please provide details (min 20 characters).';
    if (!email || !email.includes('@')) e.email = 'A valid email is required.';
    if (!phone || phone.replace(/\s/g, '').length < 10) e.phone = 'A valid phone number is required.';
    setErrors(e); return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true); setSubmitResult(null);
    try { setSubmitResult(await submitObjection({ loanApplicationId: loanApplicationId || '', objectionType: selectedType, description: description.trim(), email, phone, supportingDocumentIds: files.map((_, i) => `obj-doc-${i + 1}`) })); }
    catch (err) { setSubmitResult({ success: false, message: err.message || 'Unable to submit.' }); }
    finally { setSubmitting(false); }
  };

  if (submitResult?.success) return (
    <div data-testid="objection-success"><div className="notice notice--success"><p><strong>Reference:</strong> {submitResult.referenceNumber}</p><p>{submitResult.message}</p></div>
    <button type="button" className="btn btn--outline" data-testid="objection-new-btn" onClick={() => { setSelectedType(''); setDescription(''); setFiles([]); setSubmitResult(null); setErrors({}); }}>Submit Another</button></div>
  );

  return (
    <>
      <p>We are here to help. If you have any issues, need loan agreements, or have credit bureau concerns, let us know.</p>
      {submitResult && !submitResult.success && <div className="notice notice--danger" data-testid="objection-error"><p>{submitResult.message}</p></div>}
      <div className="form-group">
        <label className="form-label">Select your reason</label>
        {errors.selectedType && <p className="form-error">{errors.selectedType}</p>}
        <div className="objection-types" role="radiogroup">
          {OBJECTION_CATEGORIES.map(type => (
            <div key={type.value} className={`objection-type-card ${selectedType === type.value ? 'objection-type-card--selected' : ''}`}
              data-testid={`objection-type-${type.value}`}
              onClick={() => { setSelectedType(type.value); setErrors(p => ({ ...p, selectedType: undefined })); }}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedType(type.value); } }}
              role="radio" aria-checked={selectedType === type.value} tabIndex={0}>
              <div className="objection-type-card__radio" />
              <div className="objection-type-card__content">
                <span className="objection-type-card__label">{type.label}</span>
                {selectedType === type.value && <p className="objection-type-card__hint">{type.hint}</p>}
              </div>
            </div>
          ))}
        </div>
      </div>
      {selectedType && (
        <>
          <div className="form-group"><label className="form-label" htmlFor="obj-details">Provide details</label>
            <textarea id="obj-details" data-testid="objection-details" className={`form-textarea ${errors.description ? 'form-textarea--error' : ''}`} placeholder="Explain your issue..." rows={4} value={description} onChange={e => setDescription(e.target.value)} />
            <p className="form-hint">{description.length}/20 characters minimum</p>{errors.description && <p className="form-error">{errors.description}</p>}
          </div>
          <div className="form-group"><label className="form-label">Supporting documents (optional, max 5, 10MB each)</label><DocumentUpload files={files} onChange={setFiles} /></div>
          <hr className="section-divider" />
          <ContactFields email={email} phone={phone} onEmailChange={onEmailChange} onPhoneChange={onPhoneChange} errors={{ email: errors.email, phone: errors.phone }} />
          <button type="button" className="btn btn--primary" data-testid="object-btn" onClick={handleSubmit} disabled={submitting}>{submitting ? 'Submitting...' : 'Submit Objection'}</button>
        </>
      )}
    </>
  );
}

// ─── Schedule Contact Form ─────────────────────────────────────
function ScheduleContactForm({ provider, providerLabel, email, phone, onEmailChange, onPhoneChange, loanApplicationId }) {
  const [preferredDate, setPreferredDate] = useState('');
  const [preferredTime, setPreferredTime] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [errors, setErrors] = useState({});
  const today = new Date().toISOString().split('T')[0];

  const validate = () => {
    const e = {};
    if (!preferredDate) e.date = 'Please select a date.';
    if (!preferredTime) e.time = 'Please select a time.';
    if (!email || !email.includes('@')) e.email = 'A valid email is required.';
    if (!phone || phone.replace(/\s/g, '').length < 10) e.phone = 'A valid phone number is required.';
    setErrors(e); return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true); setResult(null);
    try {
      const res = await scheduleContact({ loanApplicationId: loanApplicationId || '', provider, contactNumber: phone, email, preferredDateTime: new Date(`${preferredDate}T${preferredTime}`).toISOString(), notes: notes || undefined });
      setResult(res);
    } catch (err) { setResult({ success: false, message: err.message || 'Unable to schedule.' }); }
    finally { setSubmitting(false); }
  };

  if (result?.success) return (
    <div className="notice notice--success">
      <p><strong>Reference:</strong> {result.referenceNumber}</p><p>{result.message}</p>
      <p>Scheduled: <strong>{new Date(result.confirmedDateTime).toLocaleString('en-ZA', { dateStyle: 'long', timeStyle: 'short' })}</strong></p>
      <button type="button" className="btn btn--outline" style={{ marginTop: 12 }} onClick={() => { setResult(null); setPreferredDate(''); setPreferredTime(''); setNotes(''); }}>Schedule Another</button>
    </div>
  );

  return (
    <>
      {result && !result.success && <div className="notice notice--danger"><p>{result.message}</p></div>}
      <div className="form-group"><label className="form-label" htmlFor={`sched-date-${provider}`}>Preferred date</label>
        <input id={`sched-date-${provider}`} type="date" className={`form-input ${errors.date ? 'form-input--error' : ''}`} value={preferredDate} onChange={e => setPreferredDate(e.target.value)} min={today} />{errors.date && <p className="form-error">{errors.date}</p>}
      </div>
      <div className="form-group"><label className="form-label" htmlFor={`sched-time-${provider}`}>Preferred time</label>
        <input id={`sched-time-${provider}`} type="time" className={`form-input ${errors.time ? 'form-input--error' : ''}`} value={preferredTime} onChange={e => setPreferredTime(e.target.value)} min="08:00" max="17:00" />
        <p className="form-hint">Business hours: 08:00 - 17:00</p>{errors.time && <p className="form-error">{errors.time}</p>}
      </div>
      <div className="form-group"><label className="form-label form-label--optional" htmlFor={`sched-notes-${provider}`}>Additional notes</label>
        <textarea id={`sched-notes-${provider}`} className="form-textarea" rows={2} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Topics you'd like to discuss..." />
      </div>
      <hr className="section-divider" />
      <ContactFields email={email} phone={phone} onEmailChange={onEmailChange} onPhoneChange={onPhoneChange} errors={{ email: errors.email, phone: errors.phone }} />
      <button type="button" className="btn btn--primary" onClick={handleSubmit} disabled={submitting}>{submitting ? 'Scheduling...' : `Schedule Call with ${providerLabel}`}</button>
    </>
  );
}

// ─── Counselling Content ───────────────────────────────────────
function CounsellingContent({ email, phone, onEmailChange, onPhoneChange, loanApplicationId }) {
  return (
    <>
      <p>Professional debt counselling is a legal process that can help you regain control of your finances and protect your assets.</p>

      <div className="notice notice--warning">
        <p><strong>Your rights under the National Credit Act:</strong></p>
        <ul style={{ marginTop: 8, paddingLeft: 18, marginBottom: 0 }}>
          <li>You have the right to apply for debt counselling at any time.</li>
          <li>Once under debt review, creditors may not take legal action or repossess your assets.</li>
          <li>You have the right to a restructured repayment plan.</li>
        </ul>
      </div>

      <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: 12 }}>Benefits</h3>
      <div className="counselling-info">
        {['Asset Protection', 'Reduced Monthly Payments', 'Structured Repayment Plan', 'Legal Protection', 'Reduced Interest Rates', 'Path to Financial Freedom'].map((b, i) => (
          <div key={i} className="counselling-card"><div className="counselling-card__content"><h4>{b}</h4></div></div>
        ))}
      </div>

      <div className="notice notice--info" style={{ marginTop: 16 }}>
        <p><strong>Worried about not getting loans on debt review?</strong> If your account is flagged "handed over", you also cannot get loans. Debt review protects you and provides a path back to a clean credit record.</p>
      </div>

      <hr className="section-divider" />
      <ReinventLoyaltyHighlight />

      <div className="notice notice--info" style={{ marginTop: 16 }}>
        <p><strong>Reinvent Debt Solutions</strong> is the debt counsellor we recommend for professional debt counselling.</p>
        <p className="form-hint" style={{ marginTop: 8 }}>Reinvent is an independent, registered debt counselling firm.</p>
      </div>

      <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: 12, marginTop: 24 }}>Schedule a call with Reinvent</h3>
      <ScheduleContactForm provider="reinvent" providerLabel="Reinvent Debt Solutions" email={email} phone={phone} onEmailChange={onEmailChange} onPhoneChange={onPhoneChange} loanApplicationId={loanApplicationId} />
    </>
  );
}

// ─── Contact Dola Content ──────────────────────────────────────
function ContactDolaContent({ email, phone, onEmailChange, onPhoneChange, loanApplicationId }) {
  return (
    <>
      <p>Need to speak with someone? Schedule a convenient time for us to call you back.</p>
      <ScheduleContactForm provider="dola" providerLabel="Dola Money" email={email} phone={phone} onEmailChange={onEmailChange} onPhoneChange={onPhoneChange} loanApplicationId={loanApplicationId} />
      <hr className="section-divider" />
      <div style={{ textAlign: 'center', padding: '8px 0' }}>
        <p>Or contact us directly:</p>
        <p style={{ marginTop: 8 }}><a href="tel:0115682963" className="btn btn--outline">Call 011 568 2963</a></p>
        <p style={{ marginTop: 4 }}><a href="mailto:customercare@dola.co.za" className="btn btn--outline">Email customercare@dola.co.za</a></p>
      </div>
    </>
  );
}

// ─── Risks of Default ──────────────────────────────────────────
function RisksOfDefaultContent() {
  return (
    <>
      <div style={{ marginBottom: 20 }}>
        <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: 8 }}>We Want to Help You</h3>
        <p style={{ fontSize: '0.9375rem', lineHeight: 1.6, color: '#666', marginBottom: 12 }}>
          We understand that financial difficulties can happen to anyone. This portal gives you options to find a way forward. <strong>You are not alone.</strong>
        </p>
      </div>

      <div className="notice notice--danger">
        <p><strong>Risks of remaining in default:</strong></p>
        <ol style={{ paddingLeft: 20, marginTop: 12 }}>
          <li style={{ marginBottom: 8 }}><strong>Letter of Demand</strong> from our attorneys will be sent shortly.</li>
          <li style={{ marginBottom: 8 }}><strong>Handed Over</strong> status on your credit report at all bureaux.</li>
          <li style={{ marginBottom: 8 }}><strong>No credit provider</strong> will approve new credit for you.</li>
          <li style={{ marginBottom: 8 }}><strong>Legal action</strong> may follow, with additional costs added to your account.</li>
        </ol>
      </div>

      <div className="notice notice--warning" style={{ marginTop: 16 }}>
        <p><strong>Worried about debt review affecting loan access?</strong></p>
        <p style={{ marginTop: 8 }}>If flagged "handed over", <strong>you cannot get loans anyway</strong>. Debt review provides legal protection and a clearance certificate when complete. Being "handed over" offers no path to recovery.</p>
      </div>

      <div className="notice notice--info" style={{ marginTop: 16 }}>
        <p><strong>Don't wait.</strong> The sooner you take action, the more options you have.</p>
      </div>
    </>
  );
}

// ─── SVG Icons ─────────────────────────────────────────────────
const icons = {
  settle: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>,
  arrange: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
  negotiate: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  object: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
  counselling: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>,
  contact: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>,
};

// ─── Main PortalPage ───────────────────────────────────────────
export { NegotiateContent as NegotiateSection, ObjectContent as ObjectSection };

export default function PortalPage({ token, loanApplicationId, clientData }) {
  const session = useSession();
  const { isVerified } = session;
  const [showVerification, setShowVerification] = useState(false);
  const [openSection, setOpenSection] = useState(null);
  const [email, setEmail] = useState(clientData?.accounts?.[0]?.email || '');
  const [phone, setPhone] = useState(clientData?.accounts?.[0]?.phoneNumber || '');
  const sectionRefs = useRef({});

  useEffect(() => { if (isVerified && session.email) setEmail(session.email); if (isVerified && session.phone) setPhone(session.phone); }, [isVerified, session.email, session.phone]);
  useEffect(() => { if (isVerified && showVerification) setShowVerification(false); }, [isVerified, showVerification]);

  const handleVerifyClick = useCallback(() => setShowVerification(true), []);
  const toggleSection = useCallback((id) => setOpenSection(prev => prev === id ? null : id), []);

  const handleHeaderNavigate = useCallback((sectionId) => {
    const map = { overview: 'overview', settle: 'settle', arrange: 'arrange', negotiate: 'negotiate', objection: 'object', counselling: 'counselling', contact: 'contact' };
    const mapped = map[sectionId] || sectionId;
    const requiresVerification = ['settle', 'arrange', 'negotiate', 'object'];
    if (requiresVerification.includes(mapped) && !isVerified) { setShowVerification(true); return; }
    setOpenSection(mapped);
    const el = sectionRefs.current[mapped];
    if (el) { const h = window.innerWidth >= 640 ? 64 : 56; window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - h - 8, behavior: 'smooth' }); }
  }, [isVerified]);

  if (showVerification && !isVerified) {
    return (
      <div className="app-container"><Header onNavigate={handleHeaderNavigate} />
        <main className="portal-page" role="main"><VerificationScreen />
          <button type="button" className="btn btn--outline" style={{ marginTop: 16 }} onClick={() => setShowVerification(false)}>Back to Overview</button>
        </main><Footer />
      </div>
    );
  }

  return (
    <div className="app-container">
      <Header onNavigate={handleHeaderNavigate} />
      <main className="portal-page main-content" role="main">
        <h1 className="portal-title" style={{ textAlign: 'center', fontSize: '1.5rem', fontWeight: 700, padding: '24px 16px 8px', margin: 0 }}>Manage Your Debt</h1>
        <p style={{ textAlign: 'center', color: '#666', padding: '0 16px 24px', margin: 0 }}>Review your options and take the first step towards resolving your account.</p>

        {/* Account Overview - always visible */}
        <section ref={el => (sectionRefs.current['overview'] = el)} style={{ scrollMarginTop: 72 }}>
          {clientData && <DebtOverview clientData={clientData} onVerifyClick={handleVerifyClick} />}
        </section>

        {/* Collapsible Sections */}
        <section ref={el => (sectionRefs.current['settle'] = el)} style={{ scrollMarginTop: 72 }}>
          <AccordionSection id="settle" icon={icons.settle} iconClass="accordion-trigger__icon--settle"
            title="Settle My Account" subtitle="Pay in full and resolve your account"
            isOpen={openSection === 'settle'} onToggle={toggleSection}
            requiresVerification isVerified={isVerified} onVerifyClick={handleVerifyClick}>
            <SettleContent email={email} phone={phone} onEmailChange={setEmail} onPhoneChange={setPhone} loanApplicationId={loanApplicationId} />
          </AccordionSection>
        </section>

        <section ref={el => (sectionRefs.current['arrange'] = el)} style={{ scrollMarginTop: 72 }}>
          <AccordionSection id="arrange" icon={icons.arrange} iconClass="accordion-trigger__icon--arrange"
            title="Make a Payment Arrangement" subtitle="Set up a monthly plan (min R500)"
            isOpen={openSection === 'arrange'} onToggle={toggleSection}
            requiresVerification isVerified={isVerified} onVerifyClick={handleVerifyClick}>
            <PaymentArrangementForm email={email} phone={phone} onEmailChange={setEmail} onPhoneChange={setPhone} loanApplicationId={loanApplicationId} onDone={() => toggleSection('arrange')} />
          </AccordionSection>
        </section>

        <section ref={el => (sectionRefs.current['negotiate'] = el)} style={{ scrollMarginTop: 72 }}>
          <AccordionSection id="negotiate" icon={icons.negotiate} iconClass="accordion-trigger__icon--negotiate"
            title="Negotiate Terms" subtitle="Request lower settlement or extended terms"
            isOpen={openSection === 'negotiate'} onToggle={toggleSection}
            requiresVerification isVerified={isVerified} onVerifyClick={handleVerifyClick}>
            <NegotiateContent email={email} phone={phone} onEmailChange={setEmail} onPhoneChange={setPhone} loanApplicationId={loanApplicationId} />
          </AccordionSection>
        </section>

        <section ref={el => (sectionRefs.current['object'] = el)} style={{ scrollMarginTop: 72 }}>
          <AccordionSection id="object" icon={icons.object} iconClass="accordion-trigger__icon--object"
            title="Log an Objection or Issue" subtitle="Request documents, dispute credit bureau info"
            isOpen={openSection === 'object'} onToggle={toggleSection}
            requiresVerification isVerified={isVerified} onVerifyClick={handleVerifyClick}>
            <ObjectContent email={email} phone={phone} onEmailChange={setEmail} onPhoneChange={setPhone} loanApplicationId={loanApplicationId} />
          </AccordionSection>
        </section>

        <section ref={el => (sectionRefs.current['counselling'] = el)} style={{ scrollMarginTop: 72 }}>
          <AccordionSection id="counselling" icon={icons.counselling} iconClass="accordion-trigger__icon--counselling"
            title="Debt Counselling" subtitle="Your rights, benefits, and Reinvent loyalty reward"
            isOpen={openSection === 'counselling'} onToggle={toggleSection}
            requiresVerification={false} isVerified={isVerified} onVerifyClick={handleVerifyClick}>
            <CounsellingContent email={email} phone={phone} onEmailChange={setEmail} onPhoneChange={setPhone} loanApplicationId={loanApplicationId} />
          </AccordionSection>
        </section>

        <section ref={el => (sectionRefs.current['contact'] = el)} style={{ scrollMarginTop: 72 }}>
          <AccordionSection id="contact" icon={icons.contact} iconClass="accordion-trigger__icon--contact"
            title="Contact Dola Money" subtitle="Schedule a call or reach us directly"
            isOpen={openSection === 'contact'} onToggle={toggleSection}
            requiresVerification={false} isVerified={isVerified} onVerifyClick={handleVerifyClick}>
            <ContactDolaContent email={email} phone={phone} onEmailChange={setEmail} onPhoneChange={setPhone} loanApplicationId={loanApplicationId} />
          </AccordionSection>
        </section>

        {/* Risks of Default - always visible below */}
        <section ref={el => (sectionRefs.current['risks'] = el)} style={{ scrollMarginTop: 72 }}>
          <div className="section section--warning">
            <RisksOfDefaultContent />
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
